import React from 'react';
import { Clock, CheckCircle, XCircle, User, Calendar, Mail, Phone, FileText } from 'lucide-react';
import type { JobApplication } from '../../types/applications';

interface FirebaseApplicationsListProps {
  applications: JobApplication[];
}

export default function FirebaseApplicationsList({ applications }: FirebaseApplicationsListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'withdrawn':
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'withdrawn':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (applications.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No applications yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {applications.map((application) => (
        <div key={application.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              {getStatusIcon(application.status)}
              <div>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900">Application</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(application.status)}`}>
                    {application.status}
                  </span>
                </div>
                <div className="flex items-center text-sm text-gray-500 mt-1">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>Applied {new Date(application.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          {application.resumeUrl && (
            <div className="bg-gray-50 rounded-md p-3 mb-3">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Resume</h4>
              <a
                href={application.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                <FileText className="h-4 w-4 mr-2" />
                View Resume
              </a>
            </div>
          )}

          {application.message && (
            <div className="bg-gray-50 rounded-md p-3 mb-3">
              <h4 className="text-sm font-medium text-gray-900 mb-1">Cover Letter:</h4>
              <p className="text-sm text-gray-700">{application.message}</p>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> This is a legacy application from Firebase.
              Contact information may not be available through the platform.
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}